/**
 * THE PERSON COUNT REPORT — per-camera occupancy over time, as small multiples.
 *
 * ── Why this is not the event log every other report page shows ──
 *
 * `person_counting` is a TIMER, not a detector. The AI module flushes an occupancy reading about
 * once a minute whether or not anything happened, and the snapshot it attaches is whatever frame
 * was current at the flush — the END of the minute the reading covers. So the log showed rows
 * saying "3 people" beside photographs of empty corridors: the picture and the number were
 * answering different questions, and the picture is the one a reader believes. A thousand such
 * rows is also simply not a way to learn anything about a headcount.
 *
 * What the model produces is a LEVEL, sampled once a minute. This draws it as one.
 *
 * ── Small multiples, one facet per camera ──
 *
 * The counting model normally watches several disjoint areas, so there is no single "the count" —
 * there is a count per camera and a site total. Overlaying them on one set of axes would spend
 * colour on camera identity and turn to spaghetti at the fifth camera; a facet per camera keeps
 * every shape readable at any camera count, and lets the FACET TITLE carry identity so the mark
 * itself needs only one hue.
 *
 * ONE SHARED Y-SCALE ACROSS EVERY FACET. This is the whole point of the arrangement: "the main
 * gate is three times the lobby" has to be legible as a difference in height, and per-facet scales
 * would draw a camera that peaks at 1 and one that peaks at 40 as the same picture. The cost is
 * that a quiet camera is a low line — so every facet also states its own peak and current figure
 * in text, and the reader can recover a magnitude the shared scale has flattened.
 *
 * ── A STEP line, and a broken one ──
 *
 * STEP, because occupancy persists: four people in a room at 14:00 are the same four at 14:05
 * unless somebody moved. A smooth interpolation would invent a gradual drift between two readings
 * that actually describe two plateaux, and bars would draw a level as a row of separate arrivals.
 * This is the same mark the dashboard's Person Count card uses, deliberately — the card and the
 * report must not draw one quantity two ways.
 *
 * BROKEN, because a gap in a timer-driven reading is not a zero. If the camera or the model stops
 * reporting, the count is UNKNOWN — and a line dropped to the floor would be the product stating,
 * in its most legible register, that the site was empty during an outage. The server marks those
 * buckets `null` (see occupancySeriesService) and every path here breaks across them. Short gaps
 * are carried forward by the server rather than broken, because a ~60s flush jitters and shredding
 * every line into dashes would be its own kind of lie.
 *
 * ── Colour ──
 *
 * ONE hue, `--brand`, for every mark on the page. Identity is carried by facet titles, so colour
 * has no work to do here and is free to be the same everywhere — and it matches the dashboard's
 * Person Count card, so the two surfaces that draw this quantity draw it the same. There is no
 * severity vocabulary anywhere on this page for it to be confused with: a headcount is a
 * measurement, not an alert, and nothing here is red-for-critical.
 *
 * ── Nothing is hover-only ──
 *
 * Every figure the chart encodes is also printed: the site total's now and peak, each facet's now
 * and peak, the axis ends, the bucket width. The crosshair is for reading a SPECIFIC minute, never
 * the only way to learn what a line says.
 *
 * The crosshair is SHARED across facets, which is the one thing this arrangement can do that a
 * single overlaid chart cannot: pointing at 14:20 on any facet reads 14:20 on all of them, so
 * "where was everyone when the gate spiked" is answered by moving the pointer once.
 *
 * ── Why a report component lives under components/dashboard/ ──
 *
 * Two reasons, one of them a trap. It belongs beside MiniCharts and AiAnalyticsCards because this
 * is where the product's chart vocabulary lives, and because the Person Count CARD in that file
 * draws the same quantity — the two must not drift apart. And a `components/reports/` directory
 * cannot be used at all: the root .gitignore carries a bare `reports` pattern (meant for test
 * output, under a "# Testing reports" heading) which matches a directory of that name anywhere in
 * the tree, so a source file put there is silently untracked and never reaches anyone else.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import { ExclamationTriangleIcon, UsersIcon } from '@heroicons/react/24/outline';

/** The series colour — see the note on Colour above. */
const SERIES = 'var(--brand)';

/** Grid, baseline and the crosshair: one shade off the surface, never competing with the data. */
const TRACK = 'color-mix(in srgb, var(--muted-foreground) 20%, transparent)';

/** The area under a step line. Faint enough that two facets stacked in a grid don't read as
 *  blocks of colour, strong enough to say which side of the line is "occupied". */
const AREA = 'color-mix(in srgb, var(--brand) 13%, transparent)';

/** One camera's readings over the window. `null` is "not reporting", never zero. */
export interface OccupancyCamera {
  camera_id: string;
  values: Array<number | null>;
  /** The last MEASURED reading, or null if this camera never reported in the window. */
  now: number | null;
  peak: number;
  average: number;
  /** How many buckets were measured, vs. carried forward across a short gap. */
  readings: number;
  filled: number;
  last_seen: number | null;
}

/** The payload of GET /analytics/occupancy-series. */
export interface OccupancySeriesData {
  from: number;
  to: number;
  step: number;
  buckets: number;
  auto_bucket: boolean;
  cameras: OccupancyCamera[];
  total: { values: Array<number | null>; peak: number; now: number | null };
}

export interface OccupancyReportProps {
  data: OccupancySeriesData | null;
  loading?: boolean;
  /**
   * The request FAILED, as something an operator can act on.
   *
   * Distinct from `data: null`, and the distinction is the whole point: "the site reported no
   * occupancy readings in this range" and "nobody could be asked" are completely different facts,
   * and drawing them the same way is how a broken deployment gets read as a quiet afternoon. It
   * matters most on the cloud, where the answer travels FE → cloud backend → tunnel → node and any
   * one of those hops can be the one that is out of date or offline.
   */
  error?: string | null;
  /** cameraId → the name an operator calls it. Falls back to a short id. */
  cameraName?: (id: string) => string | undefined;
  className?: string;
}

/* ─── Formatting ─── */

/** The clock at bucket `i`. 24-hour: an axis end has room for five characters, and "08:00 AM"
 *  either wraps or truncates into a different time. */
function clockAt(from: number, step: number, i: number): string {
  return new Date((from + i * step) * 1000).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/** The full instant, for the crosshair's readout — which is the one place the day matters,
 *  because the window can span several. */
function stampAt(from: number, step: number, i: number): string {
  const d = new Date((from + i * step) * 1000);
  return `${d.toLocaleDateString([], { day: '2-digit', month: 'short' })} ${clockAt(from, step, i)}`;
}

/** "5 min", "1 hr", "15 min" — the granularity, stated because a spike shorter than one bucket
 *  is inside a single mark and the reader is entitled to know that. */
export function bucketWidthLabel(step: number): string {
  if (step % 3600 === 0) return `${step / 3600} hr`;
  if (step % 60 === 0) return `${step / 60} min`;
  return `${step} s`;
}

/** Occupancy is a headcount: always an integer, never "2.0". Averages get one decimal. */
const int = (n: number) => Math.round(n).toLocaleString();
const dec1 = (n: number) => (Math.round(n * 10) / 10).toLocaleString();

/* ─── The mark ─── */

/**
 * A step line with its area, drawn over a fixed 0..max scale, broken across `null`.
 *
 * The path is built in RUNS of consecutive non-null buckets: each run gets its own `M`, so a gap
 * is a genuine discontinuity rather than a line drawn across it. Each run also gets its own area
 * polygon closed to the baseline, which is what keeps the fill from bridging an outage.
 *
 * A run of length one still draws — as a short flat segment one bucket wide, because a single
 * reading IS a level that held for its bucket, and a lone point that renders as nothing would
 * silently drop the only thing a barely-reporting camera had to say.
 */
function StepPath({
  values,
  max,
  width,
  height,
}: {
  values: Array<number | null>;
  max: number;
  width: number;
  height: number;
}) {
  const { line, area } = useMemo(() => {
    const n = values.length;
    // Each bucket owns a SLOT of the width, and the level is held across it — this is what makes
    // the mark a step. The last bucket's slot reaches the right edge.
    const slot = n > 0 ? width / n : width;
    const y = (v: number) => height - (max > 0 ? (v / max) * height : 0);

    const lines: string[] = [];
    const areas: string[] = [];
    let run: Array<{ i: number; v: number }> = [];

    const flush = () => {
      if (!run.length) return;
      const segs: string[] = [];
      run.forEach(({ i, v }, k) => {
        const x0 = i * slot;
        const x1 = x0 + slot;
        if (k === 0) segs.push(`M${x0.toFixed(2)},${y(v).toFixed(2)}`);
        // Rise or fall at the bucket's own edge, then hold across it.
        else segs.push(`L${x0.toFixed(2)},${y(v).toFixed(2)}`);
        segs.push(`L${x1.toFixed(2)},${y(v).toFixed(2)}`);
      });
      lines.push(segs.join(' '));
      const startX = run[0].i * slot;
      const endX = run[run.length - 1].i * slot + slot;
      areas.push(`${segs.join(' ')} L${endX.toFixed(2)},${height} L${startX.toFixed(2)},${height} Z`);
      run = [];
    };

    for (let i = 0; i < n; i += 1) {
      const v = values[i];
      if (v == null) flush();
      else run.push({ i, v });
    }
    flush();

    return { line: lines.join(' '), area: areas.join(' ') };
  }, [values, max, width, height]);

  if (!line) return null;

  return (
    <>
      <path d={area} fill={AREA} stroke="none" />
      <path
        d={line}
        fill="none"
        stroke={SERIES}
        strokeWidth={2}
        strokeLinejoin="round"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
    </>
  );
}

/**
 * One plot: the step mark, a baseline, and the shared crosshair when the pointer is on the page.
 *
 * `preserveAspectRatio="none"` with a fixed viewBox lets the same path stretch to whatever width
 * the grid cell gets, so the facets stay on one scale without measuring anything.
 */
function Plot({
  values,
  max,
  height,
  hover,
  onHover,
  ariaLabel,
}: {
  values: Array<number | null>;
  max: number;
  height: number;
  hover: number | null;
  onHover: (i: number | null) => void;
  ariaLabel: string;
}) {
  const W = 300;
  const H = 100;
  const ref = useRef<HTMLDivElement>(null);

  /** Pointer x → bucket index, in the plot's own coordinates rather than the SVG's — the SVG is
   *  stretched, so its user units are not proportional to screen pixels. */
  const track = useCallback(
    (clientX: number) => {
      const el = ref.current;
      if (!el || !values.length) return;
      const r = el.getBoundingClientRect();
      if (r.width <= 0) return;
      const frac = (clientX - r.left) / r.width;
      const i = Math.floor(frac * values.length);
      onHover(Math.max(0, Math.min(values.length - 1, i)));
    },
    [values.length, onHover],
  );

  const slot = values.length > 0 ? W / values.length : W;
  const hovered = hover != null && hover >= 0 && hover < values.length ? values[hover] : null;
  // The crosshair sits at the CENTRE of the bucket it reads, not its edge — the mark is a plateau
  // spanning the slot, and a line on the boundary reads as belonging to either neighbour.
  const hx = hover != null ? hover * slot + slot / 2 : 0;
  const hy = hovered != null && max > 0 ? H - (hovered / max) * H : null;

  return (
    <div
      ref={ref}
      className="relative w-full cursor-crosshair"
      style={{ height }}
      onPointerMove={(e) => track(e.clientX)}
      onPointerLeave={() => onHover(null)}
    >
      <svg
        className="absolute inset-0 h-full w-full"
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={ariaLabel}
      >
        <StepPath values={values} max={max} width={W} height={H} />
        {/* The baseline — solid, one shade off the surface, never dashed. */}
        <line x1={0} y1={H} x2={W} y2={H} stroke={TRACK} strokeWidth={1} vectorEffect="non-scaling-stroke" />
        {hover != null && (
          <line
            x1={hx}
            y1={0}
            x2={hx}
            y2={H}
            stroke={TRACK}
            strokeWidth={1}
            vectorEffect="non-scaling-stroke"
          />
        )}
      </svg>
      {/* The read-off marker, in HTML so it keeps its shape against the stretched viewBox — an
          8px dot with a 2px surface ring, which is what lifts it off the line it sits on. */}
      {hover != null && hy != null && (
        <span
          aria-hidden
          className="pointer-events-none absolute h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full"
          style={{
            left: `${(hx / W) * 100}%`,
            top: `${(hy / H) * 100}%`,
            backgroundColor: SERIES,
            boxShadow: '0 0 0 2px var(--card)',
          }}
        />
      )}
    </div>
  );
}

/* ─── Figures ─── */

/**
 * `now · peak` for one facet, and the reason a facet can say neither.
 *
 * "—" rather than "0" when a camera never reported: those are different facts, and a report that
 * prints an outage as a zero headcount is the same mistake as a line drawn along the floor.
 */
function Figures({ now, peak, quiet }: { now: number | null; peak: number; quiet?: boolean }) {
  return (
    <span className="flex flex-none items-baseline gap-2 tabular-nums">
      <span className={`text-[13px] font-bold ${quiet ? 'text-muted-foreground' : 'text-foreground'}`}>
        {now == null ? '—' : int(now)}
      </span>
      <span className="text-[11px] text-muted-foreground">peak {int(peak)}</span>
    </span>
  );
}

/* ─── The report ─── */

export default function OccupancyReport({ data, loading, error, cameraName, className = '' }: OccupancyReportProps) {
  /** The bucket every facet is reading, shared so one pointer answers the whole page. */
  const [hover, setHover] = useState<number | null>(null);

  /**
   * The height every facet is drawn against.
   *
   * The SITE TOTAL's peak, not the busiest camera's — the total is on the same scale as the
   * facets under it, so a reader can see at a glance how much of the site's line each camera
   * contributed. `|| 1` keeps a flat-zero day from dividing by nothing.
   */
  const max = useMemo(() => {
    if (!data) return 1;
    const camPeak = data.cameras.reduce((m, c) => Math.max(m, c.peak), 0);
    return Math.max(1, data.total.peak, camPeak);
  }, [data]);

  if (loading) {
    return (
      <div className={`space-y-4 p-4 ${className}`}>
        <div className="h-28 animate-pulse rounded-lg bg-muted" />
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="h-24 animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
      </div>
    );
  }

  /* THE REQUEST FAILED — say so, and say what failed. Never the "no readings" line below: that
     line explains an ABSENCE OF ALERTS, and putting it over a broken request tells the operator
     their site was quiet when in fact nobody asked it anything. */
  if (error) {
    return (
      <div className={`flex flex-col items-center gap-2 px-4 py-16 text-center ${className}`}>
        <ExclamationTriangleIcon className="h-8 w-8 text-destructive" />
        <p className="text-[14px] font-semibold text-foreground">Couldn&apos;t read occupancy</p>
        <p className="max-w-md text-[12px] text-muted-foreground">{error}</p>
      </div>
    );
  }

  if (!data || !data.cameras.length) {
    return (
      <div className={`flex flex-col items-center gap-2 px-4 py-16 text-center ${className}`}>
        <UsersIcon className="h-8 w-8 text-muted-foreground" />
        <p className="text-[14px] font-semibold text-foreground">No occupancy readings in this range</p>
        <p className="max-w-md text-[12px] text-muted-foreground">
          The counting model reports about once a minute per camera. An empty range usually means it
          wasn&apos;t attached to a camera then, or the node wasn&apos;t running — not that nobody was
          on site.
        </p>
      </div>
    );
  }

  const { from, step, buckets, total, cameras } = data;
  const lastIdx = buckets - 1;
  /** What the crosshair is reading, or the window's own ends when it is idle. */
  const readingIdx = hover;

  return (
    <div className={`space-y-4 p-4 ${className}`} onPointerLeave={() => setHover(null)}>
      {/* ── The site total ── */}
      <section className="rounded-lg border border-border bg-card p-3">
        <header className="mb-2 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <div className="flex items-baseline gap-2">
            <h3 className="text-[13px] font-semibold text-foreground">Site total</h3>
            {/* Load-bearing: this line is the SUM of the facets below, not one camera's room. */}
            <span className="text-[11px] text-muted-foreground">
              all {cameras.length} counting {cameras.length === 1 ? 'camera' : 'cameras'}
            </span>
          </div>
          <div className="flex items-baseline gap-3 tabular-nums">
            <span className="text-[11px] text-muted-foreground">
              {readingIdx != null ? stampAt(from, step, readingIdx) : `${bucketWidthLabel(step)} buckets`}
            </span>
            <span className="text-[18px] font-bold leading-none" style={{ color: SERIES }}>
              {readingIdx != null
                ? total.values[readingIdx] == null
                  ? '—'
                  : int(total.values[readingIdx] as number)
                : total.now == null
                  ? '—'
                  : int(total.now)}
            </span>
            <span className="text-[11px] text-muted-foreground">
              {readingIdx != null ? 'at that time' : 'latest'} · peak {int(total.peak)}
            </span>
          </div>
        </header>
        <Plot
          values={total.values}
          max={max}
          height={104}
          hover={hover}
          onHover={setHover}
          ariaLabel={`Total occupancy across all counting cameras, ${bucketWidthLabel(step)} buckets`}
        />
        <AxisEnds from={from} step={step} last={lastIdx} />
      </section>

      {/* ── One facet per camera ──
          Ordered busiest-first by the server: the first facet is the one worth reading. */}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {cameras.map((cam) => {
          const name = cameraName?.(cam.camera_id) || `Camera ${cam.camera_id.slice(0, 8)}`;
          const at = readingIdx != null ? cam.values[readingIdx] : null;
          const silent = cam.readings === 0;
          return (
            <section key={cam.camera_id} className="rounded-lg border border-border bg-card p-3">
              <header className="mb-1.5 flex items-baseline justify-between gap-2">
                <h4 className="truncate text-[12px] font-semibold text-foreground" title={name}>
                  {name}
                </h4>
                {readingIdx != null ? (
                  <span className="flex-none text-[13px] font-bold tabular-nums" style={{ color: SERIES }}>
                    {at == null ? '—' : int(at)}
                  </span>
                ) : (
                  <Figures now={cam.now} peak={cam.peak} quiet={silent} />
                )}
              </header>
              <Plot
                values={cam.values}
                max={max}
                height={64}
                hover={hover}
                onHover={setHover}
                ariaLabel={`Occupancy at ${name}, ${bucketWidthLabel(step)} buckets, peak ${cam.peak}`}
              />
              {/* The facet's own figures in text — the shared scale flattens a quiet camera, and
                  this is where its magnitude is recoverable. `avg` is over MEASURED buckets only,
                  so a camera that reported for an hour isn't averaged across a whole day. */}
              <p className="mt-1 flex flex-wrap items-baseline gap-x-2 text-[10px] leading-[14px] text-muted-foreground">
                <span className="tabular-nums">avg {dec1(cam.average)}</span>
                <span aria-hidden>·</span>
                <span className="tabular-nums">peak {int(cam.peak)}</span>
                {cam.filled > 0 && (
                  <>
                    <span aria-hidden>·</span>
                    {/* Says how much of the line was measured. A facet that is mostly carried
                        forward is a camera that is barely reporting, and the shape of its plot
                        should not be read as though it were sampled throughout. */}
                    <span className="tabular-nums" title="Buckets with no reading, held at the last measured level">
                      {int(cam.readings)} of {int(cam.readings + cam.filled)} measured
                    </span>
                  </>
                )}
              </p>
            </section>
          );
        })}
      </div>

      <p className="text-[11px] leading-[16px] text-muted-foreground">
        Each mark is the highest reading in a {bucketWidthLabel(step)} bucket
        {data.auto_bucket ? ' (chosen to fit the range)' : ''}. All plots share one scale, topping out
        at {int(max)}. A break in a line means the camera stopped reporting — not that it counted
        nobody.
      </p>
    </div>
  );
}

/**
 * The window's ends under a plot. Both are stated because a plot with no axis is a shape with no
 * time on it: the reader can see that it got busier and has no way to say when.
 */
function AxisEnds({ from, step, last }: { from: number; step: number; last: number }) {
  return (
    <div className="mt-1 flex items-baseline justify-between text-[10px] tabular-nums text-muted-foreground">
      <span>{clockAt(from, step, 0)}</span>
      <span>{clockAt(from, step, last)}</span>
    </div>
  );
}
